import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CardContent, CardHeader, Chip, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DropboxStatusCheckAndConnect from 'src/components/cloud_services/dropbox/DropboxStatusCheckAndConnect';
import GoogleDriveStatusCheckAndConnect from 'src/components/cloud_services/googledrive/GoogleDriveStatusCheckAndConnect';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getStorages } from 'src/services/storage.service';
import showLoading from 'src/common/loading';
import LinkIcon from '@mui/icons-material/Link';
import MiroStatusAndSync from 'src/components/cloud_services/miro/MiroStatusAndSync';
import { connectService } from 'src/services/service.service';
import OnedriveConnect from 'src/components/cloud_services/onedrive/OnedriveConnect';
import OnedriveStatusAndSync from 'src/components/cloud_services/onedrive/OnedriveStatusAndSync';

// ----------------------------------------------------------------------

class StoragePage extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			storages: [],
			search: '',
			limit: 10,
			skip: 0,
			total_storages: 0,
		}

	}

	async componentDidMount() {
		await this.fetchStorages();
		// Check for url params for OAuth callbacks
		this.checkForOAuthCallbacks();
	}


	checkForOAuthCallbacks() {
		let url = new URL(window.location.href);
		let url_params = new URLSearchParams(url.search);
		let url_params_obj = Object.fromEntries(url_params.entries());

		if ('client_id' in url_params_obj && url_params_obj['client_id'] === process.env.REACT_APP_MIRO_CLIENT_ID) {
			this.miroOAuthCallbackHandler(url_params_obj);
		}
	}

	async miroOAuthCallbackHandler(url_params_obj) {
		// Show swal saying connecting Miro...
		Swal.fire({
			title: 'Connecting Miro...',
			text: 'Please wait while we connect your Miro account.',
			allowOutsideClick: false,
			didOpen: () => {
				Swal.showLoading();
			}
		});

		url_params_obj['redirect_uri'] = process.env.REACT_APP_MIRO_REDIRECT_URI;
		let response = await connectService(config.SERVICES.MIRO, url_params_obj);

		Swal.close();

		if (response.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: response.error,
				confirmButtonText: 'Ok',
				confirmButtonColor: config.primaryColor
			}).then(() => {
				// Remove url params
				window.history.replaceState({}, document.title, window.location.pathname);
			});
			return;
		}

		Swal.fire({
			icon: 'success',
			title: 'Miro Connected',
			text: 'Your Miro account has been connected successfully.',
			confirmButtonText: 'Ok',
			confirmButtonColor: config.primaryColor
		}).then(async () => {
			// Remove url params
			window.history.replaceState({}, document.title, window.location.pathname);
			await this.fetchStorages(false);
		});

	}

	async fetchStorages(showLoader = true) {
		try {

			if (showLoader)
				showLoading(true);


			const options = {
				limit: this.state.limit,
				skip: this.state.skip
			}

			if (this.state.search)
				options.search = this.state.search;

			const storages = await getStorages(options);
			if (storages.error) {

				showLoading(false);

				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: storages.error,
					confirmButtonText: 'Ok',
					confirmButtonColor: config.primaryColor
				});
				return;
			}

			this.setState({
				storages: storages.storages,
				total_storages: storages.count
			});

			showLoading(false);

		} catch (error) {
			showLoading(false);
			console.error("Error fetching storages:", error.message)
		}
	}

	render() {

		return (
			<>
				<Helmet>
					<title> Storage | {config.APP_NAME} </title>
				</Helmet>

				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Card variant='outlined'>
							{/* Default Apps */}
							<CardHeader
								title="Default Apps"
							/>
							<CardContent style={{ paddingBottom: 0 }}>
								<Stack spacing={2} direction={"row"}>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<Box sx={{ p: 1, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="dMonk Storage"
												src={require('./../assets/appIcons/dmonk.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Storage
											</Typography>
											<Typography gutterBottom variant="body1">
												dMonk
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<Box sx={{ p: 1, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Collabora Office Suite"
												src={require('./../assets/appIcons/collabora.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Office Suite
											</Typography>
											<Typography gutterBottom variant="body1">
												Collabora
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<Box sx={{ p: 1, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Mistral AI-Engine"
												src={require('./../assets/appIcons/mistral.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												AI-Engine
											</Typography>
											<Typography gutterBottom variant="body1">
												Mistral
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<Box sx={{ p: 1, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Excalidraw Sketch"
												src={require('./../assets/appIcons/excalidraw.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Sketch
											</Typography>
											<Typography gutterBottom variant="body1">
												Excalidraw
											</Typography>
										</Box>
									</Card>
								</Stack>
							</CardContent>

							{/* Connected Apps */}
							{(this.state.storages.length > 0) ? (
								<>
									<CardHeader
										title="Connected Apps"
									/>
									<CardContent style={{ paddingBottom: 0 }}>
										<Stack spacing={2} direction={"row"}>
											{(this.state.storages.map((storage, index) => {

												return (
													<Tooltip key={storage._id} title={storage.email || ''} arrow>
													<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
														<CardHeader
															sx={{ p: 1 }}
															action={
																<>
																	{(storage.type == config.STORAGE_SERVICES.GOOGLEDRIVE) ? (
																		<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLEDRIVE_CLIENT_ID}>
																			<GoogleDriveStatusCheckAndConnect
																				buttonType="icon"
																				storageId={storage._id}
																				callbackUrl={process.env.REACT_APP_GOOGLEDRIVE_CALLBACK_URL}
																			/>
																		</GoogleOAuthProvider>
																	) : null}
																	{(storage.type == config.STORAGE_SERVICES.ONEDRIVE) ? (
																		<OnedriveStatusAndSync
																			serviceId={storage._id}
																		/>
																	) : null}
																	{(storage.type == config.STORAGE_SERVICES.DROPBOX) ? (
																		<DropboxStatusCheckAndConnect
																			storageId={storage._id}
																			callbackUrl={process.env.REACT_APP_DROPBOX_CALLBACK_URL}
																		/>
																	) : null}
																	{(storage.type == config.SERVICES.MIRO) ? (
																		<MiroStatusAndSync
																			serviceId={storage._id}
																		/>
																	) : null}
																</>
															}
														/>
														<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
															<Box
																component="img"
																sx={{
																	height: 'auto',
																	width: 60
																}}
																alt="dMonk Storage"
																src={require(`./../assets/appIcons/${storage.type}.png`)}
															/>
														</Box>
														<Divider />
														<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center', justifyContent: 'center', height: '100%' }}>
															<Typography variant="body2" sx={{ color: 'text.secondary' }}>
																{config.SERVICE_CATEGORY_MAP[storage.type]}
															</Typography>
															<Typography gutterBottom variant="body1">
																{config.SERVICE_NAME_MAP[storage.type]}
															</Typography>
														</Box>
													</Card>
													</Tooltip>
												);
											}))}
										</Stack>
									</CardContent>
								</>
							) : null}


							{/* Apps - Available to Connect */}
							<CardHeader
								title="Apps - Available to Connect"
							/>
							<CardContent>
								<Stack spacing={2} direction={"row"}>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											action={
												<>
													<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLEDRIVE_CLIENT_ID}>
														<GoogleDriveStatusCheckAndConnect
															buttonType="icon"
															hideUnsyncButton={true}
															callbackUrl={process.env.REACT_APP_GOOGLEDRIVE_CALLBACK_URL}
														/>
													</GoogleOAuthProvider>
												</>
											}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Google Drive Storage"
												src={require('./../assets/appIcons/googledrive.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Storage
											</Typography>
											<Typography gutterBottom variant="body1">
												Google Drive
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											action={
												<>
													<OnedriveConnect />
												</>
											}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Onedrive Storage"
												src={require('./../assets/appIcons/onedrive.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Storage
											</Typography>
											<Typography gutterBottom variant="body1">
												Onedrive
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											action={
												<>
													<DropboxStatusCheckAndConnect
														buttonType="icon"
														hideUnsyncButton={true}
														callbackUrl={process.env.REACT_APP_DROPBOX_CALLBACK_URL}
													/>
												</>
											}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Dropbox Storage"
												src={require('./../assets/appIcons/dropbox.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Storage
											</Typography>
											<Typography gutterBottom variant="body1">
												Dropbox
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											action={
												<IconButton aria-label="connected"
													onClick={() => {
														window.location.href = process.env.REACT_APP_MIRO_INSTALL_URL;
													}}
												>
													<LinkIcon
														fontSize='small'
													/>
												</IconButton>
											}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Miro Workspace"
												src={require('./../assets/appIcons/miro.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Workspace
											</Typography>
											<Typography gutterBottom variant="body1">
												Miro
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											title="Coming Soon"
											titleTypographyProps={{
												textAlign: 'center',
												color: 'lightgray',
												fontWeight: 'normal',
												fontSize: '16px !important',
												marginBottom: '5px'
											}}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Notion Workspace"
												src={require('./../assets/appIcons/notion.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Workspace
											</Typography>
											<Typography gutterBottom variant="body1">
												Notion
											</Typography>
										</Box>
									</Card>
									<Card variant="outlined" sx={{ width: 120, maxWidth: 150 }}>
										<CardHeader
											sx={{ p: 1 }}
											title="Coming Soon"
											titleTypographyProps={{
												textAlign: 'center',
												color: 'lightgray',
												fontWeight: 'normal',
												fontSize: '16px !important',
												marginBottom: '5px'
											}}
										/>
										<Box sx={{ p: 2, pt: 0, justifyContent: 'center', display: 'flex' }}>
											<Box
												component="img"
												sx={{
													height: 'auto',
													width: 60,
												}}
												alt="Monday.com Team"
												src={require('./../assets/appIcons/monday.png')}
											/>
										</Box>
										<Divider />
										<Box sx={{ p: 1, alignItems: 'center', textAlign: 'center' }}>
											<Typography variant="body2" sx={{ color: 'text.secondary' }}>
												Team
											</Typography>
											<Typography gutterBottom variant="body1">
												Monday.com
											</Typography>
										</Box>
									</Card>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</>
		);
	}
}

export default function (props) {
	const navigate = useNavigate();

	return <StoragePage {...props} navigate={navigate} />;
}
